import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles, Theme, Typography } from '@material-ui/core'
import clsx from 'clsx'
import React, { FC, RefObject, useCallback, useRef, useState } from 'react'

import listingColors from 'bpm/pages/ProductsPage/ProductDetailsPage/listingColors'

import { Propertyname } from '../../../Propertyname'
import { NonMSPropertyType } from '../nonMSTypes'
import NonMSSubPropertySection from './NonMSSubPropertySection'

const { propertiesDivider, boxBorder, defaultLight } = listingColors

const useStyles = makeStyles((theme: Theme) => ({
    content: {
        marginTop: theme.spacing(1.5),
    },
    subPropertyName: {
        fontSize: pxToRem(13),
        wordBreak: 'break-word',
        paddingTop: theme.spacing(1),
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        lineHeight: pxToRem(13),
        display: '-webkit-box',
        lineClamp: 2,
        boxOrient: 'vertical',
        marginBottom: theme.spacing(1.5),
        marginRight: theme.spacing(1),
    },
    divider: {
        backgroundColor: propertiesDivider,
        marginBottom: theme.spacing(1),
    },
    nonMSProperty: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        backgroundColor: defaultLight,
        border: `1px solid ${boxBorder}`,
        borderRadius: theme.spacing(1),
        maxWidth: pxToRem(705),
        width: '100%',
        paddingTop: theme.spacing(1.125),
        paddingLeft: theme.spacing(1.875),
        marginBottom: theme.spacing(0.5),
    },
    marginBottom: {
        marginBottom: theme.spacing(1.5),
    },
}))

interface NonMSPropertySectionProps {
    property: NonMSPropertyType
    propIndex: number
    handlePropertyEdit: () => void
}

const NonMSPropertySection: FC<NonMSPropertySectionProps> = ({
    property,
    propIndex,
    handlePropertyEdit,
}) => {
    const subPropRef: RefObject<HTMLDivElement> = useRef(null)

    const classes = useStyles()

    const [isHovered, setIsHovered] = useState(false)

    const setIsPropertyHovered = useCallback(
        (hovered) => () => {
            setIsHovered(hovered)
        },
        []
    )

    return (
        <GridItem
            className={classes.nonMSProperty}
            onMouseOver={setIsPropertyHovered(true)}
            onMouseLeave={setIsPropertyHovered(false)}
        >
            <GridContainer>
                <Propertyname
                    propertyName={property.propertyName}
                    isEditable={true}
                    isHovered={isHovered}
                    onEditPropertyClick={handlePropertyEdit}
                />
                {property.subProperties.map((subProp, index) => (
                    <GridItem
                        className={classes.content}
                        xs={12}
                        key={subProp.subPropertyName}
                    >
                        <GridContainer>
                            <GridItem md={3}>
                                <HoverTooltip
                                    text={
                                        subProp.subPropertyName.length > 30
                                            ? subProp.subPropertyName
                                            : ''
                                    }
                                >
                                    <Typography
                                        ref={subPropRef}
                                        component="div"
                                        className={clsx(
                                            classes.subPropertyName,
                                            {
                                                [classes.marginBottom]:
                                                    !!subProp.optionGroups[0]
                                                        .options &&
                                                    !subProp.optionGroups[0]
                                                        .options?.length &&
                                                    index ===
                                                        property.subProperties
                                                            .length -
                                                            1,
                                            }
                                        )}
                                    >
                                        {subProp.subPropertyName}:
                                    </Typography>
                                </HoverTooltip>
                            </GridItem>
                            <NonMSSubPropertySection
                                propIndex={propIndex}
                                index={index}
                                subProperty={subProp}
                                propertyName={property.propertyName}
                            />
                        </GridContainer>
                    </GridItem>
                ))}
            </GridContainer>
        </GridItem>
    )
}

export default NonMSPropertySection
